import React, { useCallback, useMemo, useRef, useState } from 'react';
import SelectBase from '@/components/mui/Form/Select/SelectBase';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import moment from 'moment';
import { OPERATORS } from '@/components/AgGrid/helpers/filterHelpers';
import { useIntl } from 'react-intl';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { InputAdornment, IconButton } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';

function ConditionDatePicker({ type, setType, onChange, dateFrom, dateTo, format, isRangeError }) {
  const intl = useIntl();

  const [customFormat, views, openTo] = useMemo(() => {
    if (type === OPERATORS.withInYear) return ['YYYY', ['year'], 'year'];
    if (type === OPERATORS.withInMonth) return ['MM/YYYY', ['year', 'month'], 'month'];

    return [format, null, null];
  }, [type, format]);

  const fromInputRef = useRef();
  const rangeInputRef = useRef();
  const timeoutIDRef = useRef();

  const [isRangeOpen, setIsRangeOpen] = useState(false);

  const onCloseFromDatePicker = useCallback(() => {
    timeoutIDRef.current = setTimeout(() => fromInputRef.current?.focus?.(), 1);
  }, []);

  const onCloseRangeDatePicker = useCallback(() => {
    timeoutIDRef.current = setTimeout(() => rangeInputRef.current?.focus?.(), 1);
    setIsRangeOpen(false);
  }, []);

  const options = useMemo(
    () =>
      [OPERATORS.beforeOrOn, OPERATORS.afterOrOn, OPERATORS.inrange, OPERATORS.withInMonth, OPERATORS.withInYear].map(
        (operator) => ({
          value: operator,
          label: intl.formatMessage({
            id: `company.workspace.grid.filters.operator.${operator}`,
            defaultMessage: operator,
          }),
        }),
      ),
    [intl],
  );

  const fromToValue = useMemo(
    () => [dateFrom ? moment(dateFrom) : null, dateTo ? moment(dateTo) : null],
    [dateFrom, dateTo],
  );

  const handleOnRangeChange = useCallback(
    (value) => {
      const [from, to] = value;
      onChange(from, false);
      onChange(to, true);
    },
    [onChange],
  );

  return (
    <>
      <SelectBase
        fullWidth
        type="button"
        value={type}
        options={options}
        onChange={(ev) => setType(ev.target.value)}
        MenuProps={{
          className: 'ag-custom-component-popup',
          sx: {
            '& .MuiButtonBase-root': {
              fontSize: 14,
            },
          },
        }}
        FormControlProps={{
          fullWidth: true,
          sx: {
            '& .MuiInputBase-input': {
              py: 1,
              fontSize: 14,
            },
            '& .MuiInputBase-root': {
              height: 32,
            },
          },
        }}
      />

      {type === 'inRange' ? (
        <DateRangePicker
          open={isRangeOpen}
          value={fromToValue}
          format={customFormat}
          onChange={handleOnRangeChange}
          onClose={onCloseRangeDatePicker}
          inputRef={rangeInputRef}
          sx={{ width: '100%', height: 40 }}
          slots={{ field: SingleInputDateRangeField }}
          slotProps={{
            popper: {
              className: 'ag-custom-component-popup',
            },
            textField: {
              error: isRangeError && !isRangeOpen,
              fullWidth: true,
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end" onClick={() => setIsRangeOpen(!isRangeOpen)}>
                    <IconButton>
                      <EventIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
              sx: {
                '& .MuiInputBase-input': {
                  py: 1,
                  fontSize: 14,
                },
                '& .MuiInputBase-root': {
                  pr: 0,
                  height: 32,
                },
              },
            },
          }}
        />
      ) : (
        <DesktopDatePicker
          format={customFormat}
          openTo={openTo}
          onClose={onCloseFromDatePicker}
          inputRef={fromInputRef}
          sx={{ width: '100%', height: 40 }}
          value={fromToValue[0]}
          onChange={(ev) => onChange(ev, false)}
          views={views}
          slotProps={{
            popper: {
              className: 'ag-custom-component-popup',
            },
            textField: {
              fullWidth: true,
              sx: {
                '& .MuiInputBase-input': {
                  py: 1,
                  fontSize: 14,
                },
                '& .MuiInputBase-root': {
                  height: 32,
                },
              },
            },
          }}
        />
      )}
    </>
  );
}

export default ConditionDatePicker;
